import SEO from '../components/SEO';
import { tentSetupsSchema, organizationSchema, breadcrumbSchema } from '../utils/structuredData';
import React from 'react';
import { Link } from 'react-router-dom';
import { Tent, CheckCircle, Shield, Zap, FileText, Award, ArrowRight } from 'lucide-react';

const TentSetupsPage = () => {
  return (
    <div style={styles.page}>
      <SEO
        title="Tent Setup & Permitting for Dealerships — Fully Turnkey"
        description="Professional tent setup and permitting for dealership events. We handle site selection, permits, installation, signage, and all infrastructure nationwide."
        keywords="tent setup dealership event, event permitting automotive, dealership tent sale, car dealer tent event, tent setup permitting service, event infrastructure dealership, turnkey tent event"
        canonicalUrl="https://driven2win.com/services/tent-setups"
        structuredData={[tentSetupsSchema, organizationSchema, breadcrumbSchema([{name:'Home',url:'https://driven2win.com'},{name:'Services',url:'https://driven2win.com/services'},{name:'Tent Setups',url:'https://driven2win.com/services/tent-setups'}])]}
      />
      <style>{`
        @media (max-width: 768px) {
          .subpage-hero-h1 { font-size: 26px !important; letter-spacing: 0.5px !important; word-break: break-word !important; overflow-wrap: break-word !important; hyphens: auto !important; }
          .subpage-hero-sub { font-size: 15px !important; }
          .subpage-hero { padding: 60px 0 !important; }
          .subpage-section-h2 { font-size: 24px !important; }
          .subpage-section-pad { padding: 60px 0 !important; }
          .subpage-cta-h2 { font-size: 24px !important; }
          .subpage-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
      {/* Hero */}
      <section style={styles.hero}>
        <div style={styles.heroOverlay} />
        <div style={styles.container}>
          <h1 style={styles.heroH1} className="subpage-hero-h1">
            TENT SETUPS & <span style={{color: '#FFC300'}}>PERMITTING</span>
          </h1>
          <p style={styles.heroSubheadline}>
            Completely Turnkey Event Infrastructure — We Handle EVERYTHING So You Can Focus On Selling
          </p>
        </div>
      </section>

      {/* Overview */}
      <section style={styles.overviewSection}>
        <div style={styles.container}>
          <h2 style={styles.sectionH2}>
            ZERO STRESS. <span style={{color: '#FF6B35'}}>MAXIMUM IMPACT.</span>
          </h2>
          <p style={styles.bodyText}>
            Planning an event is complicated. Permits, tent rentals, signage, power, lighting, logistics — it's a NIGHTMARE if you try to do it yourself.
          </p>
          <p style={styles.bodyText}>
            <strong>That's where we come in.</strong>
          </p>
          <p style={styles.bodyText}>
            We handle <strong>EVERYTHING</strong> from start to finish. Our team manages all permits, coordinates with local authorities, installs professional tent setups, arranges power and lighting, creates eye-catching signage, and ensures PERFECT execution. You show up to a ready-to-go event that looks MASSIVE and attracts serious buyers.
          </p>
          <p style={styles.bodyText}>
            No hassle. No headaches. No surprises. Just a professional, HIGH-IMPACT event that's ready to generate sales.
          </p>
        </div>
      </section>

      {/* What's Included */}
      <section style={styles.includedSection}>
        <div style={styles.container}>
          <h2 style={styles.sectionH2}>
            WHAT'S <span style={{color: '#FF6B35'}}>INCLUDED</span>
          </h2>
          
          <div style={styles.includedGrid}>
            <IncludedCard
              icon={<FileText />}
              title="Complete Permitting"
              description="We obtain ALL required permits and handle all paperwork. Zoning, occupancy, temporary use, special event permits — we don't miss a thing. Fully compliant with local regulations."
            />
            <IncludedCard
              icon={<Tent />}
              title="Professional Tent Installation"
              description="High-quality commercial tents with weatherproofing, anchoring, and safety compliance. Sizes from 20x20 to 40x80+ depending on your inventory and venue."
            />
            <IncludedCard
              icon={<Zap />}
              title="Power & Lighting Setup"
              description="Electrical infrastructure, generators if needed, professional lighting for visibility and safety. Your event looks AMAZING day or night."
            />
            <IncludedCard
              icon={<Award />}
              title="Custom Signage & Branding"
              description="Eye-catching banners, flags, directional signage, and dealership branding. We create visual impact that screams 'BIG EVENT!' and attracts crowds."
            />
            <IncludedCard
              icon={<Shield />}
              title="Insurance & Compliance"
              description="Certificate of insurance, liability coverage, vendor compliance documentation. We handle all risk management so you're protected."
            />
            <IncludedCard
              icon={<CheckCircle />}
              title="Complete Teardown"
              description="After your event, we handle EVERYTHING — tent removal, site cleanup, final inspections. The venue is left in perfect condition."
            />
          </div>
        </div>
      </section>

      {/* Process */}
      <section style={styles.processSection}>
        <div style={styles.container}>
          <h2 style={styles.sectionH2}>
            OUR <span style={{color: '#FF6B35'}}>PROCESS</span>
          </h2>
          
          <div style={styles.timeline}>
            <TimelineStep
              number="1"
              title="Venue Assessment"
              description="We visit the event location, assess space requirements, check utilities, and identify any potential challenges."
            />
            <TimelineStep
              number="2"
              title="Permit Application"
              description="Our team submits all required permit applications to local authorities. We follow up, handle any questions, and ensure approval."
            />
            <TimelineStep
              number="3"
              title="Infrastructure Planning"
              description="We design the event layout, plan tent placement, arrange power and lighting, and coordinate signage positioning."
            />
            <TimelineStep
              number="4"
              title="Pre-Event Setup"
              description="Professional crews arrive 1-2 days before your event to install tents, power, lighting, and signage. Everything is tested and ready."
            />
            <TimelineStep
              number="5"
              title="Event Support"
              description="Our team is on-call throughout your event for any adjustments or issues. You focus on selling, we handle logistics."
            />
            <TimelineStep
              number="6"
              title="Post-Event Teardown"
              description="After the event, we dismantle everything, clean the site, and ensure the venue is restored to original condition."
            />
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section style={styles.whySection}>
        <div style={styles.container}>
          <h2 style={styles.sectionH2}>
            WHY DEALERS <span style={{color: '#FF6B35'}}>TRUST US</span>
          </h2>
          
          <div style={styles.whyGrid}>
            <WhyCard
              title="23+ Years Experience"
              description="We've set up 6,500+ events across 50 states. We know every permitting process, every venue type, and every potential issue."
            />
            <WhyCard
              title="Zero Surprises"
              description="We handle EVERYTHING. No last-minute permit denials, no missing equipment, no setup delays. It just WORKS."
            />
            <WhyCard
              title="Professional Quality"
              description="Commercial-grade tents, professional lighting, high-quality signage. Your event looks like a MAJOR PRODUCTION."
            />
            <WhyCard
              title="Compliance Experts"
              description="We ensure 100% compliance with all local regulations. No fines, no shutdowns, no legal headaches."
            />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={styles.ctaSection}>
        <div style={styles.container}>
          <h2 style={styles.ctaH2}>
            Let Us Handle The <span style={{color: '#FFC300'}}>HEAVY LIFTING</span>
          </h2>
          <p style={styles.ctaText}>
            Focus on what you do best — SELLING. We'll take care of everything else!
          </p>
          <Link to="/contact#contact-form" style={styles.btnPrimary}>
            Get Your FREE Event Quote
            <ArrowRight size={24} style={{marginLeft: '12px'}} />
          </Link>
        </div>
      </section>
    </div>
  );
};

// Components
const IncludedCard = ({ icon, title, description }) => (
  <div style={styles.includedCard}>
    <div style={styles.includedIcon}>
      {React.cloneElement(icon, { size: 48, color: '#FF6B35' })}
    </div>
    <h3 style={styles.includedTitle}>{title}</h3>
    <p style={styles.includedDescription}>{description}</p>
  </div>
);

const TimelineStep = ({ number, title, description }) => (
  <div style={styles.timelineStep}>
    <div style={styles.timelineNumber}>{number}</div>
    <div style={styles.timelineContent}>
      <h3 style={styles.timelineTitle}>{title}</h3>
      <p style={styles.timelineDescription}>{description}</p>
    </div>
  </div>
);

const WhyCard = ({ title, description }) => (
  <div style={styles.whyCard}>
    <h3 style={styles.whyTitle}>{title}</h3>
    <p style={styles.whyDescription}>{description}</p>
  </div>
);

// Styles
const styles = {
  page: {
    paddingTop: '109px',
  },
  container: {
    maxWidth: '1280px',
    margin: '0 auto',
    padding: '0 16px', // Mobile-first
  },
  hero: {
    position: 'relative',
    background: 'linear-gradient(135deg, #FF6B35 0%, #E31E24 100%)',
    padding: '100px 0',
    textAlign: 'center',
    overflow: 'hidden',
  },
  heroOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  heroH1: {
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '72px',
    color: '#FFFFFF',
    marginBottom: '24px',
    letterSpacing: '3px',
    position: 'relative',
    zIndex: 1,
  },
  heroSubheadline: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '24px',
    color: '#FFFFFF',
    maxWidth: '900px',
    margin: '0 auto',
    lineHeight: '1.6',
    position: 'relative',
    zIndex: 1,
    fontWeight: '600',
  },
  overviewSection: {
    padding: '100px 0',
    background: '#FFFFFF',
  },
  sectionH2: {
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '48px',
    color: '#1A1F3A',
    marginBottom: '32px',
    letterSpacing: '1px',
    textAlign: 'center',
  },
  bodyText: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '18px',
    lineHeight: '1.8',
    color: '#333',
    marginBottom: '20px',
    maxWidth: '900px',
    margin: '0 auto 20px',
  },
  includedSection: {
    padding: '100px 0',
    background: '#F5F5F5',
  },
  includedGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(320px, 100%), 1fr))',
    gap: '32px',
  },
  includedCard: {
    background: '#FFFFFF',
    borderRadius: '16px',
    padding: '40px 32px',
    textAlign: 'center',
    border: '3px solid #E5E5E5',
  },
  includedIcon: {
    marginBottom: '20px',
  },
  includedTitle: {
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '28px',
    color: '#1A1F3A',
    marginBottom: '12px',
    letterSpacing: '1px',
  },
  includedDescription: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '15px',
    lineHeight: '1.7',
    color: '#666',
  },
  processSection: {
    padding: '100px 0',
    background: '#FFFFFF',
  },
  timeline: {
    maxWidth: '800px',
    margin: '0 auto',
  },
  timelineStep: {
    display: 'flex',
    gap: '32px',
    marginBottom: '48px',
    alignItems: 'flex-start',
  },
  timelineNumber: {
    width: '60px',
    height: '60px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #FF6B35 0%, #E31E24 100%)',
    color: '#FFFFFF',
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    boxShadow: '0 4px 16px rgba(227, 30, 36, 0.3)',
  },
  timelineContent: {
    flex: 1,
  },
  timelineTitle: {
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '32px',
    color: '#1A1F3A',
    marginBottom: '12px',
    letterSpacing: '1px',
  },
  timelineDescription: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '16px',
    lineHeight: '1.7',
    color: '#666',
  },
  whySection: {
    padding: '100px 0',
    background: '#F5F5F5',
  },
  whyGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))',
    gap: '32px',
  },
  whyCard: {
    background: '#FFFFFF',
    borderRadius: '16px',
    padding: '40px 32px',
    border: '3px solid #FF6B35',
    borderTop: '8px solid #FF6B35',
  },
  whyTitle: {
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '28px',
    color: '#FF6B35',
    marginBottom: '12px',
    letterSpacing: '1px',
  },
  whyDescription: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '15px',
    lineHeight: '1.7',
    color: '#666',
  },
  ctaSection: {
    background: 'linear-gradient(135deg, #1A1F3A 0%, #2C2C2C 100%)',
    padding: '100px 0',
    textAlign: 'center',
  },
  ctaH2: {
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '56px',
    color: '#FFFFFF',
    marginBottom: '24px',
    letterSpacing: '2px',
  },
  ctaText: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '22px',
    color: '#E5E5E5',
    marginBottom: '48px',
    fontWeight: '600',
  },
  btnPrimary: {
    display: 'inline-flex',
    alignItems: 'center',
    background: 'linear-gradient(135deg, #FF6B35 0%, #E31E24 100%)',
    color: '#FFFFFF',
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '24px',
    padding: '20px 48px',
    border: 'none',
    borderRadius: '8px',
    textDecoration: 'none',
    boxShadow: '0 6px 24px rgba(227, 30, 36, 0.4)',
    transition: 'all 0.3s ease',
    letterSpacing: '1px',
  },
};

export default TentSetupsPage;
